import { modalAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system';

import { colors, defaultGradient } from 'utils/themeConstants';

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(parts.keys);

const baseStyle = definePartsStyle({
  overlay: {
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(1px)',
  },
  header: {
    w: 'full',
    px: { base: '4', md: '7' },
    py: '4',
    position: 'relative',
    fontFamily: 'heading',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    fontSize: { base: '1.125rem', md: '1.5rem' },
    lineHeight: { base: '1.5rem', md: '1.75rem' },
    fontWeight: '400',
    textTransform: 'uppercase',
    color: 'white.95',
  },
  closeButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    _hover: {
      background: 'rgba(255, 255, 255, 0.2)',
    },
    transition: 'background 0.2s ease-in-out',
    position: 'absolute',
    right: { base: '4', md: '7' },
    top: '50%',
    transform: 'translateY(-50%)',
    borderRadius: '4px',
    width: { base: '30px', md: '36px' },
    height: { base: '36px', md: '64px' },
  },
  dialog: {
    borderRadius: '8px',
    borderTop: '2px solid rgba(255, 255, 255, 0.1)',
    pt: '0',
    bg: 'darkVoid.100',
    backdropFilter: 'blur(10px)',
    position: 'relative',
  },
  dialogContainer: {
    px: { base: '4', md: 0 },
  },
  body: {
    p: '4',
    display: 'flex',
    gap: '4',
  },
  footer: {
    p: '4',
    borderTop: '1.5px solid rgba(255, 255, 255, 0.1)',
    borderTopRadius: '8px',
  },
});

const fullScreen = definePartsStyle({
  overlay: {
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(1px)',
  },
  header: {
    w: 'full',
    px: { base: 8, md: '1rem' },
    pl: { base: '3.5rem', md: '7rem' },
    position: { base: 'relative', md: 'relative' },
    top: { base: 0, md: 'auto' },
    fontFamily: 'heading',
    borderRadius: '8px',
    background:
      'linear-gradient(180deg, #121314 0%, #141516 50%, #1D1D1E 100%)',
    borderTop: '1.5px solid rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    fontSize: { base: '1.125rem', md: '1.5rem' },
    lineHeight: { base: '1.5rem', md: '1.75rem' },
    fontWeight: '400',
    textTransform: 'uppercase',
    color: 'white.95',
    zIndex: { base: 10, md: 1 },
  },
  closeButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    _hover: {
      background: 'rgba(255, 255, 255, 0.2)',
    },
    transition: 'background 0.2s ease-in-out',
    position: 'absolute',
    left: { base: '4', md: '7' },
    top: '50%',
    transform: 'translateY(-50%)',
    borderRadius: '4px',
    width: { base: '30px', md: '60px' },
    height: { base: '36px', md: '46px' },
  },
  dialog: {
    borderRadius: '8px',
    borderTop: '2px solid rgba(255, 255, 255, 0.1)',
    padding: { base: '0', md: '80px' },
    bg: 'darkVoid.100',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    height: { base: '100dvh', md: 'auto' },
    maxHeight: { base: '100dvh', md: '85vh' },
    display: 'flex',
    flexDirection: 'column',
  },
  dialogContainer: {
    height: 'full',
    px: { base: 0, md: 0 },
  },
  body: {
    display: 'flex',
    gap: '4',
    p: { base: 4, md: 4 },
    mt: 0,
    w: 'full',
    flex: { base: 1, md: 'auto' },
    overflowY: { base: 'auto', md: 'visible' },
    overflowX: { base: 'hidden', md: 'visible' },
  },
  footer: {
    p: '4',
    borderTop: '1.5px solid rgba(255, 255, 255, 0.1)',
    borderTopRadius: '8px',
  },
});

const grey = definePartsStyle({
  dialog: {
    bg: `eerieBlack.100`,
  },
});

const tickets = definePartsStyle({
  header: {
    textTransform: 'none',
    paddingLeft: '1rem',
    textAlign: 'left',
    borderBottom: 'none',
  },
  closeButton: {
    border: 'none',
    bg: 'white.10',
  },
});

const vext = definePartsStyle({
  ...tickets,
  dialog: {
    bg: 'linear-gradient(81.73deg, #7873FC -122.13%, #000000 46.69%, #000000 57.14%, #3D06BC 212.04%)',
  },
});

const darkGrey = definePartsStyle({
  dialog: {
    bg: `darkVoid.100`,
  },
  header: {
    bg: `darkVoid.100`,
    border: 'none',
    textTransform: 'none',
    paddingLeft: '1rem',
    textAlign: 'left',
    color: 'white.95',
  },
  closeButton: {
    border: 'none',
    bg: 'white.10',
  },
  body: {
    borderBottom: `1px solid ${colors.bloodMoon[100]}`,
  },
});

const prizePool = definePartsStyle({
  dialog: {
    bg: 'darkVoid.100',
    border: '1.5px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
  },
  header: {
    bg: 'linear-gradient(90deg, rgba(128, 128, 128, 0.8) 0%, rgba(163, 163, 163, 0) 100%)',
    border: 'none',
    borderTopRadius: '4px',
    textTransform: 'none',
    padding: '0.52rem',
    textAlign: 'left',
    color: 'white.95',
  },
  closeButton: {
    border: 'none',
    borderRadius: '4px',
    bg: 'white.10',
    _hover: {
      bg: 'white.20',
    },
  },
  body: {
    background:
      'linear-gradient(180deg, rgba(38, 166, 154, 0) 0%, rgba(38, 166, 154, 0.2) 99.99%)',
    borderRadius: '8px',
  },
});

const tournament = definePartsStyle({
  dialog: {
    bg: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(20px)',
    borderRadius: '8px',
    border: '1.5px solid rgba(255, 255, 255, 0.1)',
  },
  header: {
    bg: 'linear-gradient(90deg, rgba(128, 128, 128, 0.8) 0%, rgba(163, 163, 163, 0) 100%)',
    border: 'none',
    borderTopRadius: '4px',
    textTransform: 'none',
    padding: '0.75rem',
    textAlign: 'left',
    color: 'white.95',
  },
  closeButton: {
    border: 'none',
    borderRadius: '4px',
    bg: 'white.10',
    _hover: {
      bg: 'white.20',
    },
    transition: 'background 0.2s ease-in-out',
    position: 'absolute',
    right: { base: '0', md: '0' },
    top: '50%',
    transform: 'translateY(-50%)',
    width: { base: '40px', md: '36px' },
    height: { base: '48px', md: '48px' },
  },
  body: {
    borderRadius: '8px',
  },
});

const feedbackMOdal = definePartsStyle({
  dialog: {
    bg: `rgba(0, 0, 0, 0.6)`,
    backdropFilter: 'blur(20px)',
    borderRadius: '8px',
  },
  header: {
    bg: `linear-gradient(90deg, rgba(128, 128, 128, 0.80) 0%, rgba(163, 163, 163, 0.00) 100%)`,
    border: 'none',
    textAlign: 'left',
    color: 'white.90',
    borderRadius: '8px',
    textTransform: 'none',
  },
  closeButton: {
    borderRadius: '8px',
    border: 'none',
    bg: 'rgba(255, 255, 255, 0.10)',
  },
  body: {
    borderBottom: `none`,
  },
  overlay: {
    backdropFilter: 'blur(20px)',
  },
  footer: {
    borderRadius: '8px',
  },
});

const walletModal = definePartsStyle({
  dialog: {
    bg: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(20px)',
    w: '375px',
    border: 'none',
  },
  header: {
    bg: 'linear-gradient(90deg, rgba(128, 128, 128, 0.8) 0%, rgba(163, 163, 163, 0) 100%)',
    border: 'none',
    textAlign: 'left',
    color: 'white.95',
    borderTopLeftRadius: '8px',
    fontSize: '14px',
    fontWeight: '400',
    textTransform: 'none',
    padding: '12px',
  },
  body: {
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    p: 0,
  },
  closeButton: {
    border: 'none',
    bg: 'rgba(255, 255, 255, 0.1)',
    borderTopRightRadius: '8px',
    borderTopLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    borderBottomLeftRadius: '4px',
    right: 0,
    height: '42px',
  },
});

const rewardsModal = definePartsStyle({
  dialog: {
    background: 'rgba(26, 26, 26, 1)',
    backdropFilter: 'blur(20px)',
  },
  header: {
    border: 'none',
  },
  dialogContainer: {
    px: { base: 4, md: 0 },
  },
});

const ticketsModal = definePartsStyle({
  dialog: {
    background:
      'linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.4)), linear-gradient(45.92deg, rgba(173, 53, 41, 0) 40%, rgba(216, 56, 50, 0.3) 70%, rgba(226, 100, 90, 0.6) 100%)',
    borderRadius: '8px',
    border: '1.5px solid rgba(255, 255, 255, 0.1)',
    backgroundOrigin: 'border-box',
    backdropFilter: 'blur(30px)',
  },
  header: {
    background:
      'linear-gradient(90deg, rgba(128, 128, 128, 0.8) 0%, rgba(163, 163, 163, 0) 100%)',
    border: 'none',
    textTransform: 'none',
    paddingLeft: '1rem',
    textAlign: 'left',
    color: 'white.95',
    borderTopRadius: '8px',
  },
  closeButton: {
    border: 'none',
    bg: 'white.10',
    borderTopRightRadius: '8px',
    height: '52px',
    width: '46px',
    right: 0,
  },
  footer: {
    background:
      'linear-gradient(90deg, rgba(216, 56, 50, 0) 0%, rgba(173, 53, 41, 0.6) 100%)',
    p: '1rem',
  },
});

const thumbnailModal = definePartsStyle({
  header: {
    background:
      'linear-gradient(90deg, rgba(128, 128, 128, 0.8) 0%, rgba(163, 163, 163, 0) 100%)',
    border: 'none',
    textTransform: 'none',
    paddingLeft: '1rem',
    textAlign: 'left',
    color: 'white.95',
    borderTopRadius: '8px',
  },
  closeButton: {
    border: 'none',
    bg: 'white.10',
    borderTopRightRadius: '8px',
  },
  dialog: {
    background:
      'linear-gradient(180deg, rgba(0, 0, 0, 0.9), rgba(79, 197, 186, 0.3) 99.99%)',
    backdropFilter: 'blur(20px)',
  },
});

const liveryModal = definePartsStyle({
  dialog: {
    background:
      'linear-gradient(180deg, #121314 0%, #141516 50%, #1D1D1E 100%)',
    backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
  },
  header: {
    background:
      'linear-gradient(90deg, rgba(79, 197, 186, 0.8) 0%, rgba(79, 197, 186, 0) 100%)',
    border: 'none',
    textTransform: 'none',
    paddingLeft: '1rem',
    padding: '0.6rem',
    textAlign: 'left',
    color: 'white.95',
    borderTopRadius: '8px',
  },
  closeButton: {
    border: 'none',
    bg: 'white.10',
    borderRadius: '4px',
  },
});

const repairModal = definePartsStyle({
  dialog: {
    background:
      'linear-gradient(180deg, #121314 0%, #141516 50%, #1D1D1E 100%)',
    backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
  },
  header: {
    background: 'linear-gradient(90deg, #D83832 0%, rgba(216, 56, 50, 0) 100%)',
    border: 'none',
    textTransform: 'none',
    paddingLeft: '1rem',
    padding: '0.6rem',
    textAlign: 'left',
    color: 'white.95',
    borderTopRadius: '8px',
  },
  closeButton: {
    border: 'none',
    bg: 'rgba(216, 56, 50, 0.4)',
    borderRadius: '4px',
  },
});

export const modalTheme = defineMultiStyleConfig({
  baseStyle,
  variants: {
    grey,
    darkGrey,
    feedbackMOdal,
    vext,
    tickets,
    tournament,
    walletModal,
    rewardsModal,
    ticketsModal,
    prizePool,
    thumbnailModal,
    liveryModal,
    repairModal,
    fullScreen,
  },
});
